import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { restaurantService } from "../api/services/restaurantService";
import {
  Restaurant,
  RestaurantFilters,
  RestaurantResult,
} from "../schemas/restaurantSchema";

export const useRestaurants = (
  filters: RestaurantFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: restaurants,
    isLoading: isRestaurantsLoading,
    refetch,
  } = useQuery<RestaurantResult, Error>({
    queryKey: ["restaurants", filters],
    queryFn: () => restaurantService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const useGetRestaurantById = (id?: string) =>
    useQuery<Restaurant, Error>({
      queryKey: ["restaurant", id],
      queryFn: () => restaurantService.getById(id!),
      enabled: !!id,
    });

  const createRestaurantMutation = useMutation({
    mutationFn: restaurantService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurants"] });
      queryClient.invalidateQueries({ queryKey: ["restaurantOptions"] });
      toast.success("Restaurant created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create restaurant");
    },
  });

  const updateRestaurantMutation = useMutation({
    mutationFn: restaurantService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurants"] });
      queryClient.invalidateQueries({ queryKey: ["restaurantOptions"] });
      toast.success("Restaurant updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update restaurant");
    },
  });

  const deleteRestaurantMutation = useMutation({
    mutationFn: restaurantService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["restaurants"] });
      queryClient.invalidateQueries({ queryKey: ["restaurantOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Restaurant deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete restaurant");
    },
  });

  return {
    restaurants,
    isLoading:
      isRestaurantsLoading ||
      createRestaurantMutation.isPending ||
      updateRestaurantMutation.isPending ||
      deleteRestaurantMutation.isPending,
    useGetRestaurantById,
    refetchRestaurants: refetch,
    createRestaurant: createRestaurantMutation.mutate,
    updateRestaurant: updateRestaurantMutation.mutate,
    deleteRestaurants: deleteRestaurantMutation.mutate,
  };
};
